import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import { ICON, GOAL_STATUS } from 'resources/constants';

import { useText, useFormatNumber } from 'common/hooks';
import { getIndicator, getIndicatorConfig } from 'utils/common';

import {
  Modal,
  IconWrapper,
  Icon,
  InnerWrapper,
  Title,
  RadiosWrapper,
  RadioWrapper,
  Radio,
  IconTitleWrapper,
  FadedIcon,
  FadedTitle,
  FadedText,
  Text,
  Footer,
} from './elements';

const deps = {
  goals: state.challenge.info.computedGoals,
  currentGoal: state.challenge.info.computedCurrentGoal,
  totalUserStats:
    state.challenge.stats.total.profiles[state.appContainer.user.uid],
  indicator: state.challenge.campaign.indicator,
  startOfDays: state.challenge.campaign.challenge.startOfDays,
  saveGoalClicked: sequences`challenge.info.saveGoalClicked`,
  closeModalClicked: sequences`appContainer.closeModalClicked`,
};

export function GoalModal({
  goals,
  currentGoal,
  totalUserStats,
  indicator,
  startOfDays = [],
  saveGoalClicked,
  closeModalClicked,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const { unitPath } = getIndicatorConfig(indicator);
  const formatNumber = useFormatNumber();
  const totalUserSavings = getIndicator(totalUserStats, indicator);
  const [selectedGoal, setSelectedGoal] = useState(1);

  useEffect(() => {
    if (currentGoal.status === GOAL_STATUS.CURRENT) {
      setSelectedGoal(currentGoal.goal.level);
    } else {
      setSelectedGoal(goals.findIndex(goal => goal.canBeSet) + 1);
    }
  }, [currentGoal, totalUserSavings]);

  const challengeLength = startOfDays.length;

  return (
    <Modal onHide={() => closeModalClicked()} show>
      <InnerWrapper>
        <IconWrapper>
          <Icon icon={ICON.trackChanges} color={'#fff'} />
        </IconWrapper>
        <Title>{t('challenge.GoalModal.set_goal')}</Title>
        <RadiosWrapper>
          {goals.map(({ level, score, canBeSet }) => {
            const formattedGoal = formatNumber(score);

            if (!canBeSet) {
              return (
                <RadioWrapper key={`goal-locked-${level}`}>
                  <IconTitleWrapper>
                    <FadedIcon icon={ICON.checkCircle} />
                    <FadedTitle>
                      {t(`challenge.GoalModal.${level}.title`)}
                    </FadedTitle>
                  </IconTitleWrapper>
                  <FadedText>
                    {t(`challenge.GoalModal.${level}.text`, {
                      goal: formattedGoal,
                      unit: t(unitPath),
                      length: challengeLength,
                    })}
                  </FadedText>
                </RadioWrapper>
              );
            }

            return (
              <RadioWrapper key={`goal-selector-${level}`}>
                <Radio
                  onClick={() => setSelectedGoal(level)}
                  label={t(`challenge.GoalModal.${level}.title`)}
                  checked={selectedGoal === level}
                />
                <Text onClick={() => setSelectedGoal(level)}>
                  {t(`challenge.GoalModal.${level}.text`, {
                    goal: formattedGoal,
                    unit: t(unitPath),
                    length: challengeLength,
                  })}
                </Text>
              </RadioWrapper>
            );
          })}
        </RadiosWrapper>
        <Text>{t('challenge.GoalModal.amend_this')}</Text>
      </InnerWrapper>
      <Footer
        cancelButtonText={t('challenge.GoalModal.cancel')}
        okButtonText={t('challenge.GoalModal.save_goal')}
        onClick={() =>
          saveGoalClicked({
            level: selectedGoal,
            status: currentGoal.status,
            goalKey: currentGoal?.goal?.goalKey,
          })
        }
        onCancel={() => closeModalClicked()}
      />
    </Modal>
  );
}

export default connect(deps, GoalModal);
